import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CalendarDays, ChevronRight, LogOut, Printer, UserPlus } from 'lucide-react';
import { ApiService } from '../utils/api';
import { formatDate } from '../utils/datetime';
import OfflineScanStatus from '../components/OfflineScanStatus';
import { useAuth } from '../contexts/AuthContext';
import { eventRef, useEventRoute } from '../utils/eventRef';

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-900">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white" />
  </div>
);

const ActionCard = ({ to, icon: Icon, title, description }) => (
  <Link
    to={to}
    className="group flex items-center gap-5 rounded-2xl bg-white/95 p-6 sm:p-8 shadow-xl ring-1 ring-black/5 transition hover:-translate-y-0.5 hover:shadow-2xl focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-400"
  >
    <span className="flex h-14 w-14 sm:h-16 sm:w-16 flex-shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white">
      <Icon className="h-7 w-7 sm:h-8 sm:w-8" />
    </span>
    <span className="flex-1 min-w-0">
      <span className="block text-lg sm:text-xl font-semibold text-gray-900">{title}</span>
      <span className="block text-sm text-gray-600 mt-1">{description}</span>
    </span>
    <ChevronRight className="h-6 w-6 text-gray-400 transition group-hover:translate-x-1 group-hover:text-blue-600" />
  </Link>
);

const DeskShell = ({ background, children }) => {
  const { logout, isAuthenticated } = useAuth();
  return (
    <div
      className="relative min-h-screen bg-slate-900 bg-cover bg-center"
      style={background ? { backgroundImage: `url(${background})` } : undefined}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-900/40 to-slate-900/80" />
      <div className="relative z-10 mx-auto flex min-h-screen max-w-3xl flex-col px-4 py-6 sm:py-10">
        <div className="flex justify-end min-h-[2.5rem]">
          {isAuthenticated && (
          <>
          <Link
            to="/admin/account"
            className="mr-2 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-sm text-white backdrop-blur hover:bg-white/25"
          >
            Account
          </Link>
          <button
            type="button"
            onClick={logout}
            className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-sm text-white backdrop-blur hover:bg-white/25"
          >
            <LogOut className="h-4 w-4" />
            Log out
          </button>
          </>
          )}
        </div>
        <div className="flex flex-1 flex-col justify-center gap-8 py-8">{children}</div>
      </div>
    </div>
  );
};

/** Landing page for viewer-only staff: on-spot registration and badge printing. */
const ViewerDesk = () => {
  const { eventId, eventRef } = useEventRoute();
  const { isAuthenticated } = useAuth();

  const { data: eventsResponse, isLoading: eventsLoading } = useQuery({
    queryKey: ['events', 'desk'],
    queryFn: () => ApiService.getEvents(0, 50),
    enabled: !eventId,
  });
  const { data: event, isLoading: eventLoading } = useQuery({
    queryKey: ['publicEvent', eventId],
    queryFn: () => ApiService.getPublicEvent(eventId),
    enabled: !!eventId,
  });
  const { data: purposes = [] } = useQuery({
    queryKey: ['qrScanPurposes', eventId],
    queryFn: () => ApiService.getQRScanPurposes(eventId),
    enabled: !!eventId,
  });

  if (!eventId) {
    if (eventsLoading) return <Spinner />;
    const events = eventsResponse?.content || [];
    if (events.length === 1) return <Navigate to={`/desk/${events[0].id}`} replace />;
    return (
      <DeskShell>
        <h1 className="text-center text-3xl font-bold text-white">Choose an event</h1>
        {events.length === 0 ? (
          <p className="text-center text-white/80">No events have been shared with you yet.</p>
        ) : (
          <div className="space-y-4">
            {events.map((e) => (
              <ActionCard
                key={e.id}
                to={`/desk/${eventRef(e)}`}
                icon={CalendarDays}
                title={e.name}
                description={e.eventDate ? formatDate(e.eventDate) : 'Open the event desk'}
              />
            ))}
          </div>
        )}
      </DeskShell>
    );
  }

  if (eventLoading) return <Spinner />;

  // Kiosk visitors (public desk, no login) only get on-spot when the event allows public on-spot.
  const canOnSpot = isAuthenticated || event?.onSpotAccess === 'PUBLIC';

  const badgePurpose = purposes.find((p) => p.purpose === 'badge')
    || purposes.find((p) => p.shouldPrintBadge)
    || { purpose: 'badge' };

  return (
    <DeskShell background={event?.pageBackgrounds?.VIEWER_HOME}>
      <div className="text-center text-white">
        <h1 className="text-3xl sm:text-4xl font-bold drop-shadow">{event?.name || 'Event desk'}</h1>
        {event?.eventDate && (
          <p className="mt-2 text-white/80">
            {event.eventEndDate
              ? `${formatDate(event.eventDate)} – ${formatDate(event.eventEndDate)}`
              : formatDate(event.eventDate)}
          </p>
        )}
      </div>
      <div className="space-y-4">
        {canOnSpot && (
          <ActionCard
            to={`/register/${eventRef}/on-spot?enableCta=true&from=desk`}
            icon={UserPlus}
            title="On-spot registration"
            description="Register a walk-in attendee and print their badge."
          />
        )}
        <ActionCard
          to={`/admin/events/${eventRef}/scan?purpose=${encodeURIComponent(badgePurpose.purpose)}&from=desk`}
          icon={Printer}
          title="Print badge"
          description="Search or scan an attendee and print their badge."
        />
      </div>
      <OfflineScanStatus eventId={eventId} offlineEnabled={!!event?.offlineEnabled} />
    </DeskShell>
  );
};

export default ViewerDesk;
